import type {
  InsoleDesignState,
  PrescriptionDeepState,
} from './prescriptionState';
import { CARD_IDS } from './cardIds';

export const KID_COLOR_LABEL: Record<'pink' | 'blue' | 'red', string> = {
  pink: 'Marbled pink',
  blue: 'Marbled blue',
  red: 'Marbled red',
};

/**
 * Shared summarizers for the Review step + the Order detail "Order summary"
 * card. Both consume the same PrescriptionDeepState shape - at wizard time
 * via `state.prescription.deep`, and at order-detail time via the persisted
 * `line.configuration.deep` JSONB.
 */

export function summarizeTopCover(d: InsoleDesignState | undefined): string {
  if (!d) return '-';
  const parts: string[] = [];
  if (d.top_cover_patient_type) {
    parts.push(d.top_cover_patient_type === 'adult' ? 'Adult' : 'Kid');
  }
  const mat = d.top_cover_material === 'Poron 3mm' ? 'Poron' : d.top_cover_material;
  if (mat) parts.push(mat);
  if (d.top_cover_hardness) parts.push(`${d.top_cover_hardness} Sh.A`);
  if (d.top_cover_layer && d.top_cover_layer !== 'None') parts.push(d.top_cover_layer);
  if (d.top_cover_color) parts.push(d.top_cover_color);
  if (d.top_cover_kid_color) {
    const kid = KID_COLOR_LABEL[d.top_cover_kid_color];
    if (kid) parts.push(kid);
  }
  return parts.length ? parts.join(' · ') : '-';
}

export interface ModEntry {
  label: string;
  detail: string;
  /** DOM id of the corresponding card in the prescription form, so the
   *  prescription assistant chips can scroll-to-card. */
  cardId?: string;
  /** Which sides carry the modification - used by chips to render a
   *  compact 'Both' / 'L only' / 'R only' badge. */
  sides?: 'both' | 'left' | 'right' | undefined;
}

/** Combine left/right truthy flags into the 'sides' badge value. */
function whichSides(left: boolean, right: boolean): ModEntry['sides'] {
  if (left && right) return 'both';
  if (left) return 'left';
  if (right) return 'right';
  return undefined;
}

/** Format Shore A hardness values for display ("35° Shore A"). */
export function formatHardness(v: string): string {
  if (v === 'shore_a_35') return '35° Shore A';
  if (v === 'shore_a_65') return '65° Shore A';
  return v;
}

/** Format extension softness: either Shore A hardness or rigidity scale. */
export function formatExtensionSoftness(v: string | undefined): string | undefined {
  if (!v) return undefined;
  if (v === 'shore_a_35' || v === 'shore_a_65') return formatHardness(v);
  if (v === 'rigid') return 'Rigid';
  if (v === 'semi_flexible') return 'Semi-flexible';
  if (v === 'flexible') return 'Flexible';
  return v;
}

function formatGait(v: string | undefined | null): string | undefined {
  if (!v || v === 'none') return undefined;
  if (v === 'external') return 'external gait plate';
  if (v === 'internal') return 'internal gait plate';
  // Legacy values pass through verbatim.
  return v.replace(/_/g, ' ');
}

function formatCutOut(v: string | undefined): string {
  if (!v || v === 'none') return '-';
  if (v === '1st_ray') return '1st Ray Cut Out';
  if (v === '1st_mtpj') return '1st MTPJ Cut Out';
  return v;
}

/** Metatarsal cut-out location enum -> display label. */
export function formatMcoLocation(v: string): string {
  if (v === 'cutout_m1') return 'Cutout at M1';
  if (v === 'cutout_m2') return 'Cutout at M2';
  if (v === 'cutout_m23') return 'Cutout at M23';
  if (v === 'cutout_m234') return 'Cutout at M234';
  if (v === 'cutout_m4') return 'Cutout at M4';
  if (v === 'cutout_m5') return 'Cutout at M5';
  return v;
}

/** Metatarsal cut-out softness enum -> "Light-soft (EVA 35 Shore A)" etc. */
export function formatMcoSoftness(v: string): string {
  if (v === 'carbosan_15_sa') return 'Very soft (Carbosan 15 Shore A)';
  if (v === 'eva_35_sa') return 'Light-soft (EVA 35 Shore A)';
  if (v === 'eva_65_sa') return 'Medium-soft (EVA 65 Shore A)';
  return v;
}

export function summarizeModifications(
  deep: PrescriptionDeepState | undefined,
): ModEntry[] {
  if (!deep) return [];
  const entries: ModEntry[] = [];

  const rf = deep.rearfoot;
  if (rf) {
    const sub = rf.subcalcaneal_offloading;
    if (sub) {
      const hasL =
        !!sub.left && !!sub.left.type && sub.left.type !== 'none';
      const hasR =
        !!sub.right && !!sub.right.type && sub.right.type !== 'none';
      const desc = (['left', 'right'] as const)
        .map((k) => {
          const side = sub[k];
          if (!side || !side.type || side.type === 'none') return null;
          const tag = k === 'left' ? 'L' : 'R';
          if (side.type === 'heel_offloading') {
            const bits = [`${tag}: heel offloading`];
            if (side.depth_mm != null) bits.push(`${side.depth_mm}mm`);
            if (side.fill && side.fill !== 'none') bits.push(side.fill.replace('_', ' '));
            return bits.join(' · ');
          }
          if (side.type === 'heel_cutout') {
            const bits = [`${tag}: heel cutout`];
            if (side.dimension) bits.push(side.dimension);
            if (side.position) bits.push(side.position);
            return bits.join(' · ');
          }
          return null;
        })
        .filter(Boolean)
        .join(' / ');
      if (desc) {
        entries.push({
          label: 'Subcalcaneal offloading',
          detail: desc,
          cardId: CARD_IDS.subcalcanealOffloading,
          sides: whichSides(hasL, hasR),
        });
      }
    }
    const hr = rf.heel_raise;
    if (hr) {
      const hasL = !!hr.left_type && hr.left_type !== 'none';
      const hasR = !!hr.right_type && hr.right_type !== 'none';
      const desc = (['left', 'right'] as const)
        .map((k) => {
          const tag = k === 'left' ? 'L' : 'R';
          const type = k === 'left' ? hr.left_type : hr.right_type;
          if (!type || type === 'none') return null;
          const length = k === 'left' ? hr.left_length : hr.right_length;
          const height = k === 'left' ? hr.left_height_mm : hr.right_height_mm;
          const bits = [`${tag}: ${type}`];
          if (length) bits.push(length);
          if (height != null) bits.push(`${height}mm`);
          return bits.join(' · ');
        })
        .filter(Boolean)
        .join(' / ');
      if (desc) {
        entries.push({
          label: 'Heel raise',
          detail: desc,
          cardId: CARD_IDS.heelRaise,
          sides: whichSides(hasL, hasR),
        });
      }
    }
    const rp = rf.rearfoot_posting;
    if (rp && (rp.left_enabled === false || rp.right_enabled === false)) {
      // Only surfaces when explicitly opted out (default is enabled both).
      entries.push({
        label: 'Rearfoot posting',
        detail: `L: ${rp.left_enabled === false ? 'No' : 'Yes'} / R: ${rp.right_enabled === false ? 'No' : 'Yes'}`,
        cardId: CARD_IDS.rearfootPosting,
        sides: whichSides(rp.left_enabled === false, rp.right_enabled === false),
      });
    }
    const rw = rf.rearfoot_wedge;
    if (rw) {
      const hasL = !!rw.left_type && rw.left_type !== 'none';
      const hasR = !!rw.right_type && rw.right_type !== 'none';
      const wedgeTypeLabel = (
        t: typeof rw.left_type,
      ): string | null => {
        if (!t || t === 'none') return null;
        if (t === 'kirby_skive' || t === 'intrinsic') return 'Kirby skive';
        if (t === 'rearfoot_post_intrinsic') return 'Rearfoot post';
        if (t === 'rearfoot_eva_post_extrinsic' || t === 'extrinsic') return 'Rearfoot EVA post';
        return t;
      };
      const desc = (['left', 'right'] as const)
        .map((k) => {
          const tag = k === 'left' ? 'L' : 'R';
          const type = k === 'left' ? rw.left_type : rw.right_type;
          const label = wedgeTypeLabel(type);
          if (!label) return null;
          const correction = k === 'left' ? rw.left_correction : rw.right_correction;
          const degrees = k === 'left' ? rw.left_degrees : rw.right_degrees;
          const depth = k === 'left' ? rw.left_depth_mm : rw.right_depth_mm;
          const bits = [`${tag}: ${label}`];
          if (correction) bits.push(`${correction} skive`);
          if (degrees != null) bits.push(`${degrees}°`);
          if (depth != null) bits.push(`${depth}mm`);
          return bits.join(' · ');
        })
        .filter(Boolean)
        .join(' / ');
      if (desc) {
        entries.push({
          label: 'Rearfoot wedge',
          detail: desc,
          cardId: CARD_IDS.rearfootWedge,
          sides: whichSides(hasL, hasR),
        });
      }
    }
    if (rf.observations?.trim()) {
      entries.push({ label: 'Rearfoot notes', detail: rf.observations.trim() });
    }
  }

  const mf = deep.midfoot;
  if (mf) {
    // Arch height adjustment (MLA + LLA per side)
    const aha = mf.arch_height_adjustment;
    if (aha) {
      const sideDesc = (
        archKind: 'mla' | 'lla',
      ): string => {
        return (['left', 'right'] as const)
          .map((k) => {
            const tag = k === 'left' ? 'L' : 'R';
            const levelKey = `${archKind}_${k}_level` as const;
            const heightKey = `${archKind}_${k}_height_mm` as const;
            const level = aha[levelKey];
            const height = aha[heightKey];
            if ((!level || level === 'none') && height == null) return null;
            const bits = [`${tag}:`];
            if (level) bits.push(level);
            if (height != null) bits.push(`${height}mm`);
            return bits.join(' ');
          })
          .filter(Boolean)
          .join(' / ');
      };
      const mlaDesc = sideDesc('mla');
      if (mlaDesc) {
        const mlaL = !!(aha.mla_left_level && aha.mla_left_level !== 'none') || aha.mla_left_height_mm != null;
        const mlaR = !!(aha.mla_right_level && aha.mla_right_level !== 'none') || aha.mla_right_height_mm != null;
        entries.push({
          label: 'Arch height (MLA)',
          detail: mlaDesc,
          cardId: CARD_IDS.archHeightAdjustment,
          sides: whichSides(mlaL, mlaR),
        });
      }
      const llaDesc = sideDesc('lla');
      if (llaDesc) {
        const llaL = !!(aha.lla_left_level && aha.lla_left_level !== 'none') || aha.lla_left_height_mm != null;
        const llaR = !!(aha.lla_right_level && aha.lla_right_level !== 'none') || aha.lla_right_height_mm != null;
        entries.push({
          label: 'Arch height (LLA)',
          detail: llaDesc,
          cardId: CARD_IDS.archHeightAdjustment,
          sides: whichSides(llaL, llaR),
        });
      }
    }

    // Fascial accommodation (per side, with depth when Yes)
    const fa = mf.fascial_accommodation;
    if (fa) {
      const hasL = !!fa.left && fa.left !== 'none';
      const hasR = !!fa.right && fa.right !== 'none';
      const desc = (['left', 'right'] as const)
        .map((k) => {
          const tag = k === 'left' ? 'L' : 'R';
          const opt = k === 'left' ? fa.left : fa.right;
          if (!opt || opt === 'none') return null;
          const depth = k === 'left' ? fa.left_depth_mm : fa.right_depth_mm;
          const optLabel = opt === 'yes_poron' ? 'Yes + Poron' : 'Yes';
          return depth != null ? `${tag}: ${optLabel} ${depth}mm` : `${tag}: ${optLabel}`;
        })
        .filter(Boolean)
        .join(' / ');
      if (desc) {
        entries.push({
          label: 'Fascial accommodation',
          detail: desc,
          cardId: CARD_IDS.fascialAccommodation,
          sides: whichSides(hasL, hasR),
        });
      }
    }

    // MLA extra filled (None / 3mm red PORON 12°)
    const mef = mf.mla_extra_filled;
    if (mef) {
      const labelFor = (v: typeof mef.left): string | null => {
        if (!v || v === 'none') return null;
        if (v === 'poron_3mm_12deg') return '3mm Poron';
        return v;
      };
      const leftLabel = labelFor(mef.left);
      const rightLabel = labelFor(mef.right);
      if (leftLabel || rightLabel) {
        entries.push({
          label: 'MLA extra filled',
          detail: `L: ${leftLabel ?? '-'} / R: ${rightLabel ?? '-'}`,
          cardId: CARD_IDS.mlaExtraFilled,
          sides: whichSides(!!leftLabel, !!rightLabel),
        });
      }
    }

    const fl = mf.medial_flange;
    if (fl && (fl.left_enabled || fl.right_enabled)) {
      entries.push({
        label: 'Medial flange',
        detail: `L: ${fl.left_enabled ? 'Yes' : 'No'} / R: ${fl.right_enabled ? 'Yes' : 'No'}`,
        cardId: CARD_IDS.medialFlange,
        sides: whichSides(!!fl.left_enabled, !!fl.right_enabled),
      });
    }
    const lf = mf.lateral_flange;
    if (lf && (lf.left_enabled || lf.right_enabled)) {
      entries.push({
        label: 'Lateral flange',
        detail: `L: ${lf.left_enabled ? 'Yes' : 'No'} / R: ${lf.right_enabled ? 'Yes' : 'No'}`,
        cardId: CARD_IDS.lateralFlange,
        sides: whichSides(!!lf.left_enabled, !!lf.right_enabled),
      });
    }

    // Arch reinforcement (combined MLA + LLA, Yes/No toggle per side).
    // Reads the boolean keys; falls back to the legacy enum so drafts
    // saved before the toggle refactor still summarise correctly.
    const ar = mf.arch_reinforcement;
    if (ar) {
      const sideOn = (kind: 'mla' | 'lla', k: 'left' | 'right'): boolean => {
        const boolKey = `${kind}_${k}_enabled` as const;
        if (ar[boolKey] != null) return ar[boolKey] === true;
        const legacyKey = `${kind}_${k}_level` as const;
        const level = ar[legacyKey];
        return !!(level && level !== 'none');
      };
      const arSideDesc = (kind: 'mla' | 'lla'): string => {
        return (['left', 'right'] as const)
          .map((k) => (sideOn(kind, k) ? (k === 'left' ? 'L: Yes' : 'R: Yes') : null))
          .filter(Boolean)
          .join(' / ');
      };
      const mlaDesc = arSideDesc('mla');
      if (mlaDesc) {
        entries.push({
          label: 'Arch reinforcement (MLA)',
          detail: mlaDesc,
          cardId: CARD_IDS.archReinforcement,
          sides: whichSides(sideOn('mla', 'left'), sideOn('mla', 'right')),
        });
      }
      const llaDesc = arSideDesc('lla');
      if (llaDesc) {
        entries.push({
          label: 'Arch reinforcement (LLA)',
          detail: llaDesc,
          cardId: CARD_IDS.archReinforcement,
          sides: whichSides(sideOn('lla', 'left'), sideOn('lla', 'right')),
        });
      }
    }
    if (mf.observations?.trim()) {
      entries.push({ label: 'Midfoot notes', detail: mf.observations.trim() });
    }
  }

  const ff = deep.forefoot;
  if (ff) {
    // R30 - Combined Forefoot wedge (intrinsic / extrinsic + correction +
    // height + optional hardness for extrinsic).
    const wedge = ff.forefoot_wedge;
    if (wedge) {
      const hasL = !!wedge.left_type && wedge.left_type !== 'none';
      const hasR = !!wedge.right_type && wedge.right_type !== 'none';
      const desc = (['left', 'right'] as const)
        .map((k) => {
          const tag = k === 'left' ? 'L' : 'R';
          const type = k === 'left' ? wedge.left_type : wedge.right_type;
          if (!type || type === 'none') return null;
          const correction = k === 'left' ? wedge.left_correction : wedge.right_correction;
          const height = k === 'left' ? wedge.left_height_mm : wedge.right_height_mm;
          const hardness = k === 'left' ? wedge.left_hardness : wedge.right_hardness;
          const bits = [`${tag}: ${type}`];
          if (correction) bits.push(`${correction} wedge`);
          if (height != null) bits.push(`${height}mm`);
          if (hardness) bits.push(formatHardness(hardness));
          return bits.join(' · ');
        })
        .filter(Boolean)
        .join(' / ');
      if (desc) {
        entries.push({
          label: 'Forefoot wedge',
          detail: desc,
          cardId: CARD_IDS.forefootWedge,
          sides: whichSides(hasL, hasR),
        });
      }
    }

    // Gait plate
    const gp = ff.gait_plate;
    if (gp) {
      const left = gp.left ?? (gp.left_direction as typeof gp.left | undefined);
      const right = gp.right ?? (gp.right_direction as typeof gp.right | undefined);
      const hasL = !!left && left !== 'none';
      const hasR = !!right && right !== 'none';
      if (hasL || hasR) {
        entries.push({
          label: 'Gait plate',
          detail: `L: ${formatGait(left) ?? '-'} / R: ${formatGait(right) ?? '-'}`,
          cardId: CARD_IDS.gaitPlate,
          sides: whichSides(hasL, hasR),
        });
      }
    }

    const extensionDesc = (mod: typeof ff.morton_extension): string => {
      if (!mod) return '';
      return (['left', 'right'] as const)
        .map((k) => {
          const tag = k === 'left' ? 'L' : 'R';
          const type = k === 'left' ? mod.left_type : mod.right_type;
          if (!type || type === 'none') return null;
          const thickness = k === 'left' ? mod.left_thickness : mod.right_thickness;
          const softness = k === 'left' ? mod.left_softness : mod.right_softness;
          const bits = [`${tag}: ${type === 'extrinsic' ? 'EVA post' : type}`];
          if (thickness) bits.push(thickness);
          const softnessLabel = formatExtensionSoftness(softness);
          if (softnessLabel) bits.push(softnessLabel);
          return bits.join(' · ');
        })
        .filter(Boolean)
        .join(' / ');
    };

    const extSides = (mod: typeof ff.morton_extension): ModEntry['sides'] => {
      if (!mod) return undefined;
      const hasL = !!mod.left_type && mod.left_type !== 'none';
      const hasR = !!mod.right_type && mod.right_type !== 'none';
      return whichSides(hasL, hasR);
    };

    const mortonDesc = extensionDesc(ff.morton_extension);
    if (mortonDesc) {
      entries.push({
        label: 'Morton extension',
        detail: mortonDesc,
        cardId: CARD_IDS.mortonExtension,
        sides: extSides(ff.morton_extension),
      });
    }

    const cut = ff.first_ray_cut_out;
    if (cut) {
      const left = cut.left;
      const right = cut.right;
      const hasL = !!left && left !== 'none';
      const hasR = !!right && right !== 'none';
      if (hasL || hasR) {
        entries.push({
          label: '1st Ray/MTPJ Cut Out',
          detail: `L: ${formatCutOut(left)} / R: ${formatCutOut(right)}`,
          cardId: CARD_IDS.firstRayCutOut,
          sides: whichSides(hasL, hasR),
        });
      }
    }

    const metDesc = extensionDesc(ff.metatarsal_extension);
    if (metDesc) {
      entries.push({
        label: '2nd-5th metatarsal extension',
        detail: metDesc,
        cardId: CARD_IDS.metatarsalExtension,
        sides: extSides(ff.metatarsal_extension),
      });
    }
    if (ff.observations?.trim()) {
      entries.push({ label: 'Forefoot notes', detail: ff.observations.trim() });
    }
  }

  const off = deep.offloadings;
  if (off) {
    // 1. Metatarsal cut-outs (Horseshoe offloadings)
    const mco = off.metatarsal_cut_outs;
    if (mco) {
      const hasL = !!mco.left_location && mco.left_location !== 'none';
      const hasR = !!mco.right_location && mco.right_location !== 'none';
      const desc = (['left', 'right'] as const)
        .map((k) => {
          const tag = k === 'left' ? 'L' : 'R';
          const location = k === 'left' ? mco.left_location : mco.right_location;
          if (!location || location === 'none') return null;
          const softness = k === 'left' ? mco.left_softness : mco.right_softness;
          const thickness = k === 'left' ? mco.left_thickness : mco.right_thickness;
          const poron = k === 'left' ? mco.left_poron_fill : mco.right_poron_fill;
          const bits = [`${tag}: ${formatMcoLocation(location)}`];
          if (softness) bits.push(formatMcoSoftness(softness));
          if (thickness) bits.push(thickness);
          if (poron) bits.push('+ Poron fill');
          return bits.join(' · ');
        })
        .filter(Boolean)
        .join(' / ');
      if (desc) {
        entries.push({
          label: 'Metatarsal cut-outs',
          detail: desc,
          cardId: CARD_IDS.metatarsalCutOuts,
          sides: whichSides(hasL, hasR),
        });
      }
    }

    // 2. Kinetic wedge
    const kw = off.kinetic_wedge;
    if (kw) {
      const hasL = !!(kw.left_height || kw.left_softness);
      const hasR = !!(kw.right_height || kw.right_softness);
      const desc = (['left', 'right'] as const)
        .map((k) => {
          const tag = k === 'left' ? 'L' : 'R';
          const height = k === 'left' ? kw.left_height : kw.right_height;
          const softness = k === 'left' ? kw.left_softness : kw.right_softness;
          if (!height && !softness) return null;
          const bits = [`${tag}:`];
          if (height) bits.push(height);
          if (softness) bits.push(formatHardness(softness));
          return bits.join(' · ');
        })
        .filter(Boolean)
        .join(' / ');
      if (desc) {
        entries.push({
          label: 'Kinetic wedge',
          detail: desc,
          cardId: CARD_IDS.kineticWedge,
          sides: whichSides(hasL, hasR),
        });
      }
    }

    // 3-7. Cards that follow the "primary value + thickness" shape.
    // Subcapital bar is enum-typed (None / Carbosan / Poron) so the summary
    // skips the 'none' state explicitly.
    const selectThicknessCards: Array<{
      key:
        | 'subcapital_bar'
        | 'drop_met_pad'
        | 'mtt_bar'
        | 'extra_heel_cushion_pad';
      label: string;
      cardId: string;
      primaryKeys: { left: string; right: string };
      formatPrimary?: (v: string) => string;
    }> = [
      {
        key: 'subcapital_bar',
        label: 'Subcapital bar',
        cardId: CARD_IDS.subcapitalBar,
        primaryKeys: { left: 'left', right: 'right' },
        formatPrimary: (v) => (v === 'carbosan' ? 'Carbosan' : v === 'poron' ? 'Poron' : v),
      },
      {
        key: 'drop_met_pad',
        label: 'Drop met pad',
        cardId: CARD_IDS.dropMetPad,
        primaryKeys: { left: 'left_location', right: 'right_location' },
        formatPrimary: (v) => {
          if (v === 'm2_3') return '2 to 3';
          if (v === 'm3_4') return '3 to 4';
          if (v === 'm2_4') return '2 to 4';
          return v;
        },
      },
      {
        key: 'mtt_bar',
        label: 'MTT bar',
        cardId: CARD_IDS.mttBar,
        primaryKeys: { left: 'left_location', right: 'right_location' },
        formatPrimary: (v) => {
          if (v === 'm1_5') return '1 to 5';
          if (v === 'm2_5') return '2 to 5';
          if (v === 'm2_4') return '2 to 4';
          return v;
        },
      },
      {
        key: 'extra_heel_cushion_pad',
        label: 'Extra heel cushion pad',
        cardId: CARD_IDS.extraHeelCushionPad,
        primaryKeys: { left: 'left', right: 'right' },
        formatPrimary: (v) => {
          if (v === 'carbosan_standard') return 'Carbosan (standard)';
          if (v === 'poron') return 'Poron';
          return v;
        },
      },
    ];
    for (const card of selectThicknessCards) {
      const slot = off[card.key] as Record<string, unknown> | undefined;
      if (!slot) continue;
      const leftRaw = slot[card.primaryKeys.left] as string | undefined;
      const rightRaw = slot[card.primaryKeys.right] as string | undefined;
      const isReal = (v: string | undefined) => Boolean(v) && v !== 'none';
      const hasL = isReal(leftRaw);
      const hasR = isReal(rightRaw);
      if (!hasL && !hasR) continue;
      const desc = (['left', 'right'] as const)
        .map((k) => {
          const tag = k === 'left' ? 'L' : 'R';
          const raw = k === 'left' ? leftRaw : rightRaw;
          if (!isReal(raw)) return null;
          const primary = card.formatPrimary ? card.formatPrimary(raw!) : raw!;
          const tk = slot[`${k}_thickness`] as string | undefined;
          return tk ? `${tag}: ${primary} · ${tk}` : `${tag}: ${primary}`;
        })
        .filter(Boolean)
        .join(' / ');
      if (desc) {
        entries.push({
          label: card.label,
          detail: desc,
          cardId: card.cardId,
          sides: whichSides(hasL, hasR),
        });
      }
    }

    // 6. Metatarsal head cut-outs (M1-M5 checkboxes per side)
    const mhco = off.metatarsal_head_cut_outs;
    if (mhco) {
      const sideDesc = (s: typeof mhco.left): string | null => {
        if (!s) return null;
        const picked = (['m1', 'm2', 'm3', 'm4', 'm5'] as const)
          .filter((m) => s[m])
          .map((m) => m.toUpperCase());
        return picked.length ? picked.join('+') : null;
      };
      const left = sideDesc(mhco.left);
      const right = sideDesc(mhco.right);
      if (left || right) {
        entries.push({
          label: 'Metatarsal head cut-outs',
          detail: `L: ${left ?? '-'} / R: ${right ?? '-'}`,
          cardId: CARD_IDS.metatarsalHeadCutOuts,
          sides: whichSides(!!left, !!right),
        });
      }
    }
    if (off.observations?.trim()) {
      entries.push({ label: 'Offloadings notes', detail: off.observations.trim() });
    }
  }

  const sandal = deep.sandal;
  if (sandal) {
    const bits: string[] = [];
    // R36 — new configurator fields.
    if (sandal.model) bits.push(sandal.model);
    if (sandal.base_color) bits.push(`base ${sandal.base_color.replace(/_/g, ' ')}`);
    if (sandal.upper_strap_color) {
      bits.push(`strap ${sandal.upper_strap_color.replace(/_/g, ' ')}`);
    }
    if (sandal.top_cover) bits.push(`top ${sandal.top_cover}`);
    if (sandal.white_midsole) bits.push(`white midsole: ${sandal.white_midsole}`);
    if (sandal.posterior_strap) bits.push(`posterior strap: ${sandal.posterior_strap}`);
    if (sandal.upper_strap_size) {
      bits.push(
        `strap size ${sandal.upper_strap_size === 'large_cavus' ? 'large' : 'standard'}`,
      );
    }
    if (sandal.custom_name?.trim()) bits.push(`name: "${sandal.custom_name.trim()}"`);
    // Legacy pre-R36 fields, surfaced so old drafts still render readable
    // summaries during the migration window.
    if (sandal.upper_style && !sandal.model) bits.push(sandal.upper_style);
    if (sandal.color && !sandal.base_color) bits.push(sandal.color);
    if (sandal.size_eu != null) bits.push(`EU ${sandal.size_eu}`);
    if (sandal.custom_engraving) bits.push(`engraving: "${sandal.custom_engraving}"`);
    if (bits.length) entries.push({ label: 'Sandal', detail: bits.join(' · ') });
  }

  return entries;
}
